import { loadExcelJs } from "../utils/loadExcelJs.mjs";
import { currentIsoDate, parseCurrency } from "./invoiceUtils";

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const MONEY_FORMAT = '#,##0.00;[Red]-#,##0.00';

const safeFilenamePart = (value) => String(value || "invoice-studio")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-|-$/g, "");

const styleHeader = (row) => row.eachCell((cell) => {
  cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF202322" } };
  cell.alignment = { vertical: "middle" };
});

const invoiceBalance = (invoice) => {
  if (invoice.balanceAmount !== null && invoice.balanceAmount !== undefined && Number.isFinite(Number(invoice.balanceAmount))) {
    return Number(invoice.balanceAmount);
  }
  if (["Draft", "Paid", "Void"].includes(invoice.status)) return 0;
  return parseCurrency(invoice.amount) - Number(invoice.paidAmount || 0);
};

const billableAmount = (invoice) => ["Draft", "Void"].includes(invoice.status)
  ? 0
  : parseCurrency(invoice.amount);

const collectedAmount = (invoice) => {
  if (["Draft", "Void"].includes(invoice.status) || invoice.documentType === "credit-note") return 0;
  const paid = Number(invoice.paidAmount || 0);
  return paid || (invoice.status === "Paid" ? parseCurrency(invoice.amount) : 0);
};

const currencySummary = (invoices) => Object.values(invoices.reduce((summary, invoice) => {
  const currency = invoice.currency || "EUR";
  const current = summary[currency] || { currency, billed: 0, collected: 0, outstanding: 0 };
  current.billed += billableAmount(invoice);
  current.collected += collectedAmount(invoice);
  current.outstanding += invoiceBalance(invoice);
  summary[currency] = current;
  return summary;
}, {}));

export const createInvoiceRegisterWorkbook = async ({ invoices = [], businesses = [], client = null, now = new Date() } = {}) => {
  const ExcelJS = await loadExcelJs();
  const records = client
    ? invoices.filter((invoice) => invoice.clientId ? invoice.clientId === client.id : invoice.client === client.name)
    : invoices;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Invoice Studio by Coetic X";
  workbook.created = now;

  const summarySheet = workbook.addWorksheet("Summary");
  summarySheet.columns = [
    { header: "Scope", key: "scope", width: 28 },
    { header: "Currency", key: "currency", width: 12 },
    { header: "Billed", key: "billed", width: 18, style: { numFmt: MONEY_FORMAT } },
    { header: "Collected", key: "collected", width: 18, style: { numFmt: MONEY_FORMAT } },
    { header: "Outstanding", key: "outstanding", width: 18, style: { numFmt: MONEY_FORMAT } },
    { header: "Invoices", key: "invoiceCount", width: 12 },
  ];
  const scope = client?.name || "All invoice clients";
  currencySummary(records).forEach((entry) => summarySheet.addRow({ ...entry, scope, invoiceCount: records.filter((invoice) => (invoice.currency || "EUR") === entry.currency).length }));
  if (!records.length) summarySheet.addRow({ scope, currency: client?.currency || "EUR", billed: 0, collected: 0, outstanding: 0, invoiceCount: 0 });
  styleHeader(summarySheet.getRow(1));

  const registerSheet = workbook.addWorksheet(client ? "Client statement" : "Invoice register", { views: [{ state: "frozen", ySplit: 1 }] });
  registerSheet.columns = [
    { header: "Invoice number", key: "invoiceNumber", width: 18 },
    { header: "Issue date", key: "issuedDate", width: 14 },
    { header: "Due date", key: "dueDate", width: 14 },
    { header: "Issuing business", key: "issuer", width: 26 },
    { header: "Client", key: "client", width: 28 },
    { header: "Work", key: "work", width: 34 },
    { header: "Status", key: "status", width: 18 },
    { header: "Currency", key: "currency", width: 11 },
    { header: "Total", key: "total", width: 16, style: { numFmt: MONEY_FORMAT } },
    { header: "Paid", key: "paid", width: 16, style: { numFmt: MONEY_FORMAT } },
    { header: "Balance", key: "balance", width: 16, style: { numFmt: MONEY_FORMAT } },
  ];
  records.forEach((invoice) => registerSheet.addRow({
    invoiceNumber: invoice.invoiceNumber,
    issuedDate: invoice.issuedDate || "",
    dueDate: invoice.dueDate || "",
    issuer: businesses.find((business) => business.id === invoice.issuerId)?.name || "",
    client: invoice.client,
    work: invoice.work,
    status: invoice.status,
    currency: invoice.currency || "EUR",
    total: parseCurrency(invoice.amount),
    paid: Number(invoice.paidAmount || (invoice.status === "Paid" ? parseCurrency(invoice.amount) : 0)),
    balance: invoiceBalance(invoice),
  }));
  styleHeader(registerSheet.getRow(1));
  registerSheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: Math.max(1, registerSheet.rowCount), column: 11 } };

  return { workbook, records };
};

const safeDownloadFilename = (candidate, fallback) => {
  const filename = String(candidate || "").trim();
  const hasControlCharacter = [...filename].some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127);
  return filename && filename.length <= 255 && !hasControlCharacter && !filename.includes("/") && !filename.includes("\\") && filename.toLowerCase().endsWith(".xlsx")
    ? filename
    : fallback;
};

const snapshotStatus = (row, today = new Date().toISOString().slice(0, 10)) => {
  if (row.status === "paid") return "Paid";
  if (row.status === "void") return "Void";
  if (row.status === "draft") return "Draft";
  if (row.documentType === "credit_note") return "Sent";
  const paid = Number(row.paidTotal || 0);
  const total = Number(row.total || 0);
  if (paid > 0 && paid < total) return "Partially paid";
  if (row.dueOn && String(row.dueOn).slice(0, 10) < today) return "Overdue";
  return "Sent";
};

export const invoiceRegisterSnapshotToWorkbookInput = ({ rows = [], client = null } = {}) => {
  const orderedRows = [...rows].sort((left, right) => Number(left.position || 0) - Number(right.position || 0));
  const businesses = [...new Map(orderedRows.map((row) => [row.businessId, {
    id: row.businessId,
    name: row.issuerName || "Issuing business",
  }])).values()];
  return {
    businesses,
    client,
    invoices: orderedRows.map((row) => ({
      id: row.invoiceId,
      invoiceNumber: row.invoiceNumber,
      clientId: row.clientId,
      client: row.clientName || "Invoice client",
      issuerId: row.businessId,
      issuedDate: String(row.issuedOn || "").slice(0, 10),
      dueDate: String(row.dueOn || "").slice(0, 10),
      work: row.workSummary || "",
      amount: Number(row.total || 0),
      paidAmount: Number(row.paidTotal || 0),
      balanceAmount: Number(row.balance || 0),
      currency: row.currency || "EUR",
      status: snapshotStatus(row),
      documentType: row.documentType === "credit_note" ? "credit-note" : "invoice",
      archived: Boolean(row.archivedAt),
      revision: Number(row.invoiceRevision || 1),
    })),
  };
};

export const downloadInvoiceRegister = async ({ invoices, businesses, client = null, requestedFilename = "" } = {}) => {
  const { workbook, records } = await createInvoiceRegisterWorkbook({ invoices, businesses, client });
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: XLSX_MIME });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  const scope = client ? `${safeFilenamePart(client.name)}-statement` : "invoice-register";
  const filename = safeDownloadFilename(requestedFilename, `${scope}-${currentIsoDate()}.xlsx`);
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
  return { filename, recordCount: records.length };
};

export { currencySummary, invoiceBalance };
